import { useState, useEffect, useRef, useCallback } from 'react';
import { useChatPanel } from '../contexts/ChatPanelContext';
import { sendMessageStreaming, submitChatFeedback } from '../services/chatService';
import { analytics } from '../utils/analytics';
import { MessageActionHandler } from '../utils/messageActionHandler';
import CONVERSAYION_MESSAGE_TYPES from '../constants/conversationMessageTypes';

/**
 * IMPROVED: Single source of truth chat hook
 * Uses only ChatPanelContext for conversation state management
 */
const useChat = (options = {}) => {
  const {
    onActionExecute = null,
    restoreFromPanel = false,
    initialQuery = '',
    enableAutoSend = false
  } = options;

  const { chatPanelState, updateConversation, navigateWithChat, setAiThinking, setLoading, setVisitorTokenLimitReached } = useChatPanel();
  
  // Only UI state - conversation comes directly from context
  const [query, setQuery] = useState(initialQuery);
  const [feedbackSubmittingId, setFeedbackSubmittingId] = useState(null);
  // tokenLimitReached is canonical in ChatPanelContext so it persists across route changes
  const tokenLimitReached = chatPanelState.visitorTokenLimitReached || false;
  
  // Refs for lifecycle management
  const isInitialQueryRef = useRef(!!initialQuery);
  const isMounted = useRef(false);
  const messagesEndRef = useRef(null);
  const autoSendTriggeredRef = useRef(false);
  const latestThreadIdRef = useRef(null);
  const loadingRef = useRef(chatPanelState.loading);

  // Use conversation directly from context (single source of truth)
  const conversation = chatPanelState.conversation || [];
  const loading = chatPanelState.loading;
  const threadId = chatPanelState.threadId;

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (threadId) {
      latestThreadIdRef.current = threadId;
    }
  }, [threadId]);

  // Load chat history on mount - REMOVED to prevent duplicate calls
  // ChatPanelContext handles initialization globally
  useEffect(() => {
    // Prevent double call in strict mode
    if (isMounted.current) return;
    isMounted.current = true;
  }, []);



  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation.length]); // Use conversation.length to trigger on new messages

  // Removed: Sync local conversation with panel state (no longer needed)

  // Handle chat actions (navigation, etc.)
  const handleChatAction = useCallback((action) => {
    if (!action) return;

    switch (action.type?.toUpperCase()) {
      case 'NAVIGATE':
        // Use navigateWithChat to maintain chat context and show chat panel
        navigateWithChat(action.target, action.params);
        break;
      
      case 'REDIRECT':
        // Direct navigation for redirects
        const url = action.params ? `${action.target}?${action.params}` : action.target;
        window.location.href = url;
        break;
      
      // Add more action types as needed
      default:
        console.warn('Unknown action type:', action.type);
    }
  }, [navigateWithChat]);

  // Send message function - updates context directly
  const handleSendMessage = useCallback(async (e, isInitial = false, messageOverride = null, talkMode = false) => {
    console.log('🎤 useChat.handleSendMessage called with:', { e: !!e, isInitial, messageOverride: !!messageOverride, talkMode });
    
    e?.preventDefault();
    
    // Use messageOverride if provided, otherwise use query state
    const messageToSend = (messageOverride ?? query ?? '').trim();
    if (!messageToSend || loadingRef.current) {
      return false;
    }

    setLoading(true);
    setAiThinking(true); // Start AI thinking animation
    analytics.chatMessage();
    const userMessage = messageToSend;
    
    // Only clear input if we're using the query state (not override)
    if (!messageOverride) {
      setQuery(''); // Clear input immediately
    }

    // Add user message to context conversation
    updateConversation(prev => [...prev, 
      { type: 'user', text: userMessage, message_type: '' },
    ]);

    try {
      // Handle actions and update conversation
      let aiMessage = '';
      let lastAction = null;
      let hasReceivedFirstChunk = false;

      for await (const chunk of sendMessageStreaming(userMessage, 'user', null, talkMode)) {
        if (chunk?.message) {
          let message_type = chunk?.message_type || 'general';

          if (chunk?.message_type !== CONVERSAYION_MESSAGE_TYPES.HUMAN) {
            // Stop AI thinking animation on first AI response chunk
            if (!hasReceivedFirstChunk) {
              setAiThinking(false);
              hasReceivedFirstChunk = true;
            }
            
            aiMessage += chunk.message;
            
            // Track the last action
            if (chunk.action) {
              lastAction = chunk.action;
            }

            updateConversation(prev => {
              const updatedConversation = [...prev];
              const lastMessage = updatedConversation[updatedConversation.length - 1];
              const incomingVisualizations = Array.isArray(chunk.visualizations)
                ? chunk.visualizations
                : null;

              // Use the utility to process message actions
              const formattedText = MessageActionHandler.processMessageAction(
                chunk.message, 
                chunk.action
              );

              let replace_last_message = false;
              if (lastMessage.message_type === CONVERSAYION_MESSAGE_TYPES.AI_THINKING || 
                  lastMessage.message_type === CONVERSAYION_MESSAGE_TYPES.ERROR) {
                replace_last_message = true;
              }

              // Create message object with audio_data if present
              const messageObject = {
                type: 'ai', 
                text: formattedText, 
                message_type,
                action: lastAction
              };

              if (incomingVisualizations && incomingVisualizations.length > 0) {
                messageObject.visualizations = incomingVisualizations;
              } else if (Array.isArray(lastMessage?.visualizations) && lastMessage.visualizations.length > 0) {
                messageObject.visualizations = lastMessage.visualizations;
              }

              if (chunk.id) {
                messageObject.id = chunk.id;
              }

              if (chunk.thread_id) {
                messageObject.thread_id = chunk.thread_id;
                latestThreadIdRef.current = chunk.thread_id;
              }
              
              // Include audio_data if present (from talk_mode)
              if (chunk.audio_data) {
                messageObject.audio_data = chunk.audio_data;
                messageObject.audio_format = chunk.audio_format || 'mp3';
                console.log('🎤 Received audio data in talk mode:', {
                  hasAudio: !!chunk.audio_data,
                  format: chunk.audio_format,
                  audioLength: chunk.audio_data ? chunk.audio_data.length : 0
                });
              }

              if (replace_last_message) {
                updatedConversation[updatedConversation.length - 1] = { 
                  ...lastMessage, 
                  ...messageObject
                };
                return updatedConversation;
              } else {
                return [...prev, messageObject];
              }
            });
          }
        }
      }

      // Execute action if present
      if (lastAction) {
        if (onActionExecute) {
          onActionExecute(lastAction);
        } else {
          handleChatAction(lastAction);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setAiThinking(false); // Clear AI thinking on error
      if (error.status === 402 || error.message?.includes('402')) {
        setVisitorTokenLimitReached(true);
        // Remove the user message that was optimistically added
        updateConversation(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[i].text !== userMessage));
      } else {
        updateConversation(prev => [...prev, { 
          type: 'ai', 
          text: 'An error occurred while getting the response. Please try again!',
          message_type: CONVERSAYION_MESSAGE_TYPES.ERROR
        }]);
      }
    } finally {
      setLoading(false);
      setAiThinking(false); // Ensure AI thinking is cleared
    }

    return true;
  }, [query, onActionExecute, handleChatAction, updateConversation, setAiThinking, setLoading, setVisitorTokenLimitReached]);

  const handleMessageFeedback = useCallback(async (message, feedbackValue) => {
    const resolvedThreadId =
      threadId ||
      message?.thread_id ||
      latestThreadIdRef.current ||
      [...conversation].reverse().find((msg) => msg?.thread_id)?.thread_id ||
      null;

    console.log('🎤 handleMessageFeedback called with:', {
      messageId: message?.id,
      threadId,
      messageThreadId: message?.thread_id,
      resolvedThreadId,
    });

    if (!message?.id || !resolvedThreadId) {
      console.warn('Missing message id or thread id for feedback submission', {
        messageId: message?.id,
        resolvedThreadId,
      });
      return;
    }

    const nextFeedbackValue = message?.feedback_value === feedbackValue ? null : feedbackValue;

    try {
      setFeedbackSubmittingId(message.id);

      const result = await submitChatFeedback({
        thread_id: resolvedThreadId,
        message_id: message.id,
        feedback_value: nextFeedbackValue,
      });

      const savedFeedback = Object.prototype.hasOwnProperty.call(result || {}, 'feedback_value')
        ? result.feedback_value
        : nextFeedbackValue;
      updateConversation(prev => prev.map((msg) => {
        if (msg?.id === message.id) {
          return {
            ...msg,
            feedback_value: savedFeedback,
          };
        }
        return msg;
      }));
    } catch (error) {
      console.error('Failed to submit chat feedback:', error);
    } finally {
      setFeedbackSubmittingId(null);
    }
  }, [threadId, conversation, updateConversation]);

  // Handle initial query auto-send - must be after handleSendMessage definition
  useEffect(() => {
    const preparedInitialQuery = (initialQuery || '').trim();

    if (!enableAutoSend ||
        !isInitialQueryRef.current ||
        !preparedInitialQuery ||
        loading ||
        autoSendTriggeredRef.current) {
      return;
    }

    // Delay keeps StrictMode from double-sending during development remount checks.
    const timer = setTimeout(async () => {
      const didSend = await handleSendMessage(null, true, preparedInitialQuery);

      if (!didSend) {
        return;
      }

      autoSendTriggeredRef.current = true;
      isInitialQueryRef.current = false;

      // Clear one-time initial query markers after successful send.
      const historyState = window.history.state || {};
      const userState = historyState.usr || {};
      const nextUserState = { ...userState };
      const hadStateInitialQuery = Object.prototype.hasOwnProperty.call(userState, 'initialQuery');

      if (hadStateInitialQuery) {
        delete nextUserState.initialQuery;
      }

      const currentUrl = new URL(window.location.href);
      const hadQueryInitialQuery = currentUrl.searchParams.has('q');

      if (hadQueryInitialQuery) {
        currentUrl.searchParams.delete('q');
      }

      const nextSearch = currentUrl.searchParams.toString();
      const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`;

      if (hadStateInitialQuery || hadQueryInitialQuery) {
        window.history.replaceState(
          { ...historyState, usr: nextUserState },
          document.title,
          nextUrl
        );
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [enableAutoSend, initialQuery, loading, handleSendMessage]);

  return {
    // State (conversation comes directly from context)
    query,
    setQuery,
    conversation, // Direct reference to context conversation
    loading,
    tokenLimitReached,
    
    // Refs
    messagesEndRef,
    
    // Functions
    handleSendMessage,
    handleChatAction,
    handleMessageFeedback,
    feedbackSubmittingId,
  };
};

export default useChat;
